import React from "react";
import PropTypes from "prop-types";
import distanceInWordsToNow from "date-fns/distance_in_words_to_now";
import differenceInSeconds from "date-fns/difference_in_seconds";
import makeStyles from "@mui/styles/makeStyles";
//Material imports
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
// Limitless imports
import IconButton from "@limitless/modern/inputs/icon-button/IconButton";
import Grid from "@limitless/modern/layout/Grid";
import CloseIcon from "@limitless/icons/Close";
import ModernTaskListItem from "../modern-task-list-item/ModernTaskListItem";
import Typography from "@limitless/modern/data-display/typography/Typography";
import Button from "@limitless/modern/inputs/button/Button";
import Chip from "@limitless/modern/data-display/chip/Chip";

import { LoadingSpinner, ExpertConversation } from "@limitless/portal";
import { useAppConfig, useTranslation } from "@limitless/common/hooks";
import { findInboxExpiryTime } from "@limitless/expert/utils/InboxExpiryHelper";
import { useExpertProfileContext, ExpertMessage, useMessagePass, useExpertMessageContext } from "@limitless/expert";
import { locale as translatableLocale, getMessageCreatedFormatedDate } from "@limitless/common/utils";
import { TimeLeftCell } from "@limitless/portal";
import ModernTaskListAvatar from "../modern-task-avatar/ModernTasksAvatar";
import ModernTenantAvatar from "../modern-tenant-avatar/ModernTenantAvatar";
import ModernTaskListItemLock from "../modern-task-list-item/ModernTaskListItemLock";

const useStyles = makeStyles(theme => ({
    messageList: {
        boxSizing: "border-box",
        margin: "auto 0 0",
        padding: "0",
        width: "100%",
        marginTop: 0 //2px padding in ConversationDetail
    }
}));

const ModernTaskListPreview = ({
    message,
    messageDetails,
    onClose,
    tabId,
    loading,
    fadeoutItemHandler,
    type,
    feeLabel,
    rewardFee,
    points,
    balanceCurrencySymbol
}) => {
    const { countryList } = useAppConfig();
    const { passMessage } = useMessagePass();
    const { t } = useTranslation();
    const classes = useStyles();

    const expiryTime = findInboxExpiryTime(messageDetails);
    const translate = React.useMemo(() => translatableLocale(t, true), []);

    const distanceInWords = distanceInWordsToNow(new Date(expiryTime), {
        includeSeconds: true,
        addSuffix: false,
        locale: translatableLocale(t, true)
    });
    const diff = differenceInSeconds(new Date(expiryTime), new Date());
    const locale = React.useMemo(() => translatableLocale(t, true), []);
    const { profile } = useExpertProfileContext();
    const { clearMessage } = useExpertMessageContext();

    const handlePassMessage = message => {
        passMessage(true);
        clearMessage();
    };

    const renderList = React.useMemo(() => {
        return (
            <ModernTaskListItem
                tabId={tabId}
                contentVisible={false}
                message={message}
                showPassButton
                handlePassMessage={() => handlePassMessage(message)}
            />
        );
    }, [tabId, message, handlePassMessage]);

    return (
        <Dialog
            open
            PaperProps={{
                sx: { maxWidth: "804px", minWidth: "650px", minHeight: "100px", paddingBottom: 2, paddingTop: 2 }
            }}
            fullWidth
        >
            <DialogContent>
                {loading && <LoadingSpinner overlayParent size={30} color="primary" />}

                <ExpertMessage>
                    {({ message }) => {
                        const dailoguesCreationTime = getMessageCreatedFormatedDate(
                            message.created,
                            profile,
                            countryList,
                            locale
                        );

                        return (
                            (!message.loading || message.dialogues.length > 0) && (
                                <>
                                    <Grid display="flex" flexDirection="row" justifyContent="space-between">
                                        <Grid
                                            display="flex"
                                            flexDirection="row"
                                            alignItems="center"
                                            mb={1}
                                            pl={1}
                                            width="100%"
                                        >
                                            <ModernTaskListAvatar type={type} isPassed={message.passed} />

                                            <Typography color="textPrimary" variant="body1" mr={1}>
                                                {message?.displayName ? message?.displayName : message.groupName}
                                            </Typography>
                                            <ModernTenantAvatar tenantName={message?.tenantName} />
                                        </Grid>
                                        <Grid
                                            container
                                            display="flex"
                                            flexDirection="row"
                                            justifyContent="flex-end"
                                            alignItems="flex-start"
                                            pr={2}
                                        >
                                            <Typography
                                                variant="caption"
                                                mr={2}
                                                mt={"-2px"}
                                                color={
                                                    distanceInWords.includes("minute") ||
                                                    distanceInWords.includes("second")
                                                        ? "error.dark"
                                                        : "warning.grey"
                                                }
                                            >
                                                {diff < 60 ||
                                                (Number.isNaN(diff) && message.type === "respondSticky") ? (
                                                    <TimeLeftCell
                                                        value={expiryTime || 0}
                                                        localiseDate={translate?.distanceInWords.localize}
                                                    />
                                                ) : (
                                                    distanceInWords
                                                )}
                                            </Typography>
                                            <IconButton
                                                icon={<CloseIcon />}
                                                // sx={{ width: 12 }}
                                                label=""
                                                onClick={onClose}
                                                id="previewCloseIconButton"
                                            />
                                        </Grid>
                                    </Grid>
                                    {message.dialogues.length > 0 ? (
                                        <>
                                            <Grid
                                                container
                                                display="flex"
                                                flexDirection="row"
                                                alignItems="center"
                                                pl={"20px"}
                                                pr={2}
                                            >
                                                <Grid xs={5} md={5} sm={5} lg={5}>
                                                    <Typography variant="subtitle2" sx={{ marginRight: "12px" }}>
                                                        {message.author}
                                                    </Typography>
                                                </Grid>
                                            </Grid>

                                            <Grid pl={2} sx={{ maxHeight: "400px", overflow: "auto" }}>
                                                <ExpertConversation
                                                    profile={profile}
                                                    message={message}
                                                    countryList={countryList}
                                                    handleSuggestedAnswerSelection={() => null}
                                                    isParentLoading={false}
                                                    scrollOnLoad={false}
                                                    classes={classes}
                                                    showHeader={true}
                                                />
                                            </Grid>
                                            <Grid
                                                pl={2}
                                                pr={2}
                                                display="flex"
                                                flexDirection="row"
                                                alignItems="center!important"
                                                justifyContent={
                                                    feeLabel ? "space-between!important" : "flex-end!important"
                                                }
                                            >
                                                {feeLabel && (
                                                    <Chip
                                                        sx={{
                                                            backgroundColor: theme => theme.palette.secondary.light,
                                                            minWidth: 57
                                                        }}
                                                        label={feeLabel}
                                                    />
                                                )}
                                                {renderList}
                                            </Grid>
                                        </>
                                    ) : (
                                        onClose(true)
                                    )}
                                </>
                            )
                        );
                    }}
                </ExpertMessage>
                {message.claimedByAnother && <ModernTaskListItemLock onTransitionComplete={fadeoutItemHandler} />}
                {message.claimedByAnother && (
                    <Grid mt={1} container justifyContent="center">
                        <Button variant="contained" onClick={onClose}>
                            Return to inbox
                        </Button>
                    </Grid>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default ModernTaskListPreview;

ModernTaskListPreview.propTypes = {
    message: PropTypes.object.isRequired,
    messageDetails: PropTypes.object.isRequired,
    tabId: PropTypes.object.number,
    type: PropTypes.string,
    rewardFee: PropTypes.number,
    points: PropTypes.string,
    feeLabel: PropTypes.string,
    balanceCurrencySymbol: PropTypes.balanceCurrencySymbol,
    onClose: PropTypes.func.isRequired,
    fadeoutItemHandler: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired
};
